import { Icon } from "@blueprintjs/core"
import React, { useEffect, useMemo, useState } from "react"
import { useFundamentalsDisplayData } from "../../../hooks/useFundamentalsDisplayData"
import { LargeText } from "../../../styled/text.styled"
import TickerSuggest from "../Config/components/TickerSuggest"
import useTradingCompanies from "../Config/hooks/useTradingCompanies"
import { TradingCompany } from "../Config/types"
import {
  FundamentalsDisplayContainer,
  FundamentalsDisplayHeader,
  FundamentalsDisplayHeaderLeft,
  FundamentalsDisplayHeaderRight,
  FundamentalsGridContainer,
} from "./FundamentalsDisplay.styled"
import FundamentalsGrid from "./FundamentalsGrid"
import SourceFilter from "./SourceFilter"

const FundamentalsDisplay: React.FC = () => {
  const [selectedCompany, setSelectedCompany] = useState<TradingCompany | undefined>(undefined)
  const [selectedSource, setSelectedSource] = useState<string>("S14") // Default to S14 only

  // Create wrapper for TickerSuggest
  const useCompaniesWrapper = ({ query }: { query: string }) => {
    const { companies, isLoading, error } = useTradingCompanies({ query })
    return { items: companies ?? [], isLoading, error }
  }

  // Fetch fundamentals data
  const { data: fundamentalsData, hasData } = useFundamentalsDisplayData({
    company_id: selectedCompany?.id,
    enabled: Boolean(selectedCompany?.id),
  })

  // Available sources for selection (all sources including S14)
  const availableSources = useMemo(() => {
    if (!fundamentalsData?.available_sources) return ["S14"]
    return fundamentalsData.available_sources
  }, [fundamentalsData?.available_sources])

  /**
   * Effect to reset the selected source when the company changes
   */
  useEffect(() => {
    setSelectedSource("S14")
  }, [selectedCompany])

  return (
    <FundamentalsDisplayContainer flexDirection="column" gap="12px">
      <FundamentalsDisplayHeader flexDirection="row" gap="20px">
        <FundamentalsDisplayHeaderLeft flexDirection="row" gap="20px">
          <TickerSuggest<TradingCompany>
            setSelectedItem={setSelectedCompany}
            selectedItem={selectedCompany}
            useItems={useCompaniesWrapper}
            errorMessage="Unable to search companies, please try to refresh the page."
            autoFocus={false}
          />
          <SourceFilter
            availableSources={availableSources}
            selectedSource={selectedSource}
            onSourceChange={setSelectedSource}
            disabled={!hasData}
          />
        </FundamentalsDisplayHeaderLeft>
        <FundamentalsDisplayHeaderRight flexDirection="row-reverse" flexGrow={1} gap="10px">
          <LargeText>{fundamentalsData?.data?.last_updated_at}</LargeText>
          <Icon icon="calendar" />
        </FundamentalsDisplayHeaderRight>
      </FundamentalsDisplayHeader>
      <FundamentalsGridContainer
        className="ag-theme-balham-dark"
        flexGrow={1}
        flexDirection="column"
      >
        <FundamentalsGrid fundamentals={fundamentalsData} selectedSource={selectedSource} />
      </FundamentalsGridContainer>
    </FundamentalsDisplayContainer>
  )
}

export default FundamentalsDisplay
